import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageTitle as usePageTitleContext } from '../contexts/PageTitleContext';
import { apiService, AutoEncryptFolder, UpdateAutoEncryptFolderRequest } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  FolderIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  FolderOpenIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingOfficeIcon,
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const Folders: React.FC = () => {
  const { t } = useTranslation('folders');
  usePageTitle(t('title'), t('subtitle'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId?: string }>();
  const { setPageTitle } = usePageTitleContext();
  const { user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<{
    name: string;
    policy_id: string;
    include_subfolders: boolean;
    auto_delete_original: boolean;
    move_original_file: boolean;
    move_original_to_path: string;
    device_id?: string;
    is_template?: boolean;
    auto_provision_to_members?: boolean;
    for_companies?: string[];
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState<any[]>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(-1);
  const [expandedCompanies, setExpandedCompanies] = useState<Record<string, boolean>>({});
  const [filterUser, setFilterUser] = useState<boolean>(false);
  const [filterCompany, setFilterCompany] = useState<boolean>(false);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateAutoEncryptFolderRequest>({});
  const [showCreateMovePathPicker, setShowCreateMovePathPicker] = useState(false);
  const [createMovePathQuery, setCreateMovePathQuery] = useState('');
  const [showEditMovePathPicker, setShowEditMovePathPicker] = useState(false);
  const [editMovePathQuery, setEditMovePathQuery] = useState('');
  const [openFolderMenuId, setOpenFolderMenuId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{ id: string; path: string } | null>(null);
  const [delegateModalOpen, setDelegateModalOpen] = useState(false);
  const [folderToDelegate, setFolderToDelegate] = useState<{ id: string; name: string; is_template: boolean } | null>(null);

  // Fetch desktop devices (only for regular users)
  const enableDevices = user?.role === 'user';
  const { data: desktopDevices = [], error: devicesError } = useQuery({
    queryKey: ['desktop-devices'],
    queryFn: () => {
      console.log('🖥️ FOLDERS: Fetching desktop devices');
      return apiService.getDesktopDevices();
    },
    enabled: enableDevices,
  });

  // Fetch auto encrypt folders (all folders, we'll filter by device on frontend)
  const { data: allFolders = [], error: foldersError } = useQuery({
    queryKey: ['auto-encrypt-folders'],
    queryFn: () => {
      console.log('📂 FOLDERS: Fetching auto-encrypt folders');
      return apiService.getAutoEncryptFolders();
    },
  });

  // Fetch company settings for regular users (to read max_folders_per_device override)
  const { data: userCompanySettings } = useQuery({
    queryKey: ['company-settings', user?.company_id],
    queryFn: async () => {
      if (!user?.company_id) return null;
      const res = await apiService.get(`/companies/${user.company_id}`);
      const company = res.data;
      let settings = company?.settings;
      if (typeof settings === 'string') {
        try { settings = JSON.parse(settings); } catch { settings = {}; }
      }
      return settings || {};
    },
    enabled: user?.role === 'user' && !!user?.company_id,
  });

  const maxFoldersPerDevice = useMemo(() => {
    const raw = userCompanySettings?.max_folders_per_device;
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 5;
  }, [userCompanySettings]);

  // Check view mode from localStorage and listen for changes
  const [viewMode, setViewMode] = React.useState<'user' | 'admin' | 'sysadmin'>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as 'user' | 'admin' | 'sysadmin';
        if (user?.role === 'sysadmin') {
          return mode;
        } else if (user?.role === 'admin') {
          return mode === 'sysadmin' ? 'admin' : mode;
        }
        return 'user';
      }
      if (user?.role === 'admin' || user?.role === 'sysadmin') {
        return 'admin';
      }
      return 'user';
    } catch {
      return 'user';
    }
  });

  // Listen for view mode changes
  React.useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.detail.viewMode) {
        setViewMode(event.detail.viewMode);
      } else if (event.detail.isAdministrationView !== undefined) {
        setViewMode(event.detail.isAdministrationView ? 'admin' : 'user');
      }
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    window.addEventListener('administration-view-changed', handler as EventListener);
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
      window.removeEventListener('administration-view-changed', handler as EventListener);
    };
  }, []);
  
  const effectiveMode = user?.role === 'sysadmin' 
    ? viewMode 
    : (user?.role === 'admin' ? (viewMode === 'sysadmin' ? 'admin' : viewMode) : 'user');

  // Compute folders list based on role and administration view
  const folders = useMemo(() => {
    // Regular users always see only their device folders
    if (user?.role === 'user') {
      return deviceId ? allFolders.filter(folder => folder.device_id === deviceId) : [];
    }

    // For admins/sysadmins: check view mode
    if (effectiveMode === 'user' && user) {
      // User mode: show only folders owned by the current user
      return allFolders.filter(folder => folder.user_id === user.id);
    }

    // Admin or SysAdmin mode: show all folders
    return allFolders;
  }, [allFolders, user, deviceId, effectiveMode]);

  // Unique move-to-path suggestions from existing folders/templates (non-empty, move enabled)
  const moveToPathSuggestions = useMemo(() => {
    const set = new Set<string>();
    for (const f of allFolders) {
      const path = (f as any).move_original_to_path;
      const enabled = !!(f as any).move_original_file;
      if (enabled && typeof path === 'string') {
        const trimmed = path.trim();
        if (trimmed.length > 0) set.add(trimmed);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allFolders]);

  const filteredMoveToPathSuggestions = useMemo(() => {
    const q = createMovePathQuery.trim().toLowerCase();
    if (!q) return moveToPathSuggestions;
    return moveToPathSuggestions.filter(p => p.toLowerCase().includes(q));
  }, [moveToPathSuggestions, createMovePathQuery]);

  const filteredEditMoveToPathSuggestions = useMemo(() => {
    const q = editMovePathQuery.trim().toLowerCase();
    if (!q) return moveToPathSuggestions;
    return moveToPathSuggestions.filter(p => p.toLowerCase().includes(q));
  }, [moveToPathSuggestions, editMovePathQuery]);

  // Group folders by company for sysadmin
  const foldersByCompany = React.useMemo(() => {
    if (user?.role !== 'sysadmin') return null;
    
    const grouped: Record<string, AutoEncryptFolder[]> = {};
    const noCompany: AutoEncryptFolder[] = [];
    
    allFolders.forEach(folder => {
      if (folder.company_id) {
        if (!grouped[folder.company_id]) {
          grouped[folder.company_id] = [];
        }
        grouped[folder.company_id].push(folder);
      } else {
        noCompany.push(folder);
      }
    });
    
    return { grouped, noCompany };
  }, [allFolders, user?.role]);
  
  // Log folder filtering
  if (deviceId) {
    console.log(`📂 FOLDERS: Filtering folders for device ${deviceId}:`, {
      totalFolders: allFolders.length,
      filteredFolders: folders.length,
      deviceFolders: folders.map(f => ({ id: f.id, path: f.folder_path }))
    });
  } else {
    console.log('📂 FOLDERS: No device selected, showing device list:', {
      totalDevices: desktopDevices.length,
      totalFolders: allFolders.length
    });
  }

  // Handle device selection
  const handleDeviceSelect = (selectedDeviceId: string) => {
    console.log('🖥️ FOLDERS: Device selected:', selectedDeviceId);
    navigate(`/folders/device/${selectedDeviceId}`);
  };

  // Back navigation helper (removed unused handler to satisfy eslint)

  // Fetch policies for dropdown
  const { data: policies = [] } = useQuery({
    queryKey: ['policies'],
    queryFn: () => apiService.getPolicies(),
  });

  // Fetch companies for sysadmin multi-select
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiService.getCompanies(),
    enabled: user?.role === 'sysadmin',
  });

  // Precompute selected device and sync header BEFORE any early returns
  const selectedDevice = (user?.role === 'user' && deviceId)
    ? desktopDevices.find(device => device.id === deviceId)
    : undefined;
  React.useEffect(() => {
    if (selectedDevice) {
      setPageTitle(`${selectedDevice.name} - Folders`, 'Manage auto-encrypt folders for this device');
    } else if (user?.role === 'admin' || user?.role === 'sysadmin') {
      // Show different title based on view mode
      if (effectiveMode === 'admin' || effectiveMode === 'sysadmin') {
        setPageTitle('Folder Templates', 'Manage auto-encrypt folder templates');
      } else {
        setPageTitle('Folders', 'Manage your auto-encrypt folders');
      }
    } else {
      setPageTitle('Folders', 'Manage auto-encrypt folders');
    }
  }, [selectedDevice, setPageTitle, user?.role, effectiveMode]);

  // Listen for header action to open create folder modal
  React.useEffect(() => {
    const handler = () => {
      // Prevent opening create modal if regular user already hit device limit
      if (user?.role === 'user') {
        const targetDeviceId = deviceId;
        if (targetDeviceId) {
          const currentCount = allFolders.filter(f => f.device_id === targetDeviceId).length;
          if (currentCount >= maxFoldersPerDevice) {
            toast.error(`Folder limit reached: max ${maxFoldersPerDevice} auto-encrypt folders per device`);
            return;
          }
        }
      }
      setCreateForm({
        name: '',
        policy_id: '',
        include_subfolders: true,
        auto_delete_original: false,
        move_original_file: false,
        move_original_to_path: '',
        device_id: deviceId,
        is_template: (effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (user?.role === 'admin' || user?.role === 'sysadmin') ? true : false,
        auto_provision_to_members: false,
        for_companies: [],
      });
      setShowCreateMovePathPicker(false);
      setCreateMovePathQuery('');
      setSelectedCompanies([]);
      setCompanySearchTerm('');
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      setSelectedCompanyIndex(-1);
      setShowCreateModal(true);
    };
    window.addEventListener('open-create-folder' as any, handler);
    return () => window.removeEventListener('open-create-folder' as any, handler);
  }, [deviceId, user?.role, allFolders, maxFoldersPerDevice, effectiveMode]);

  const handleCreateFolder = async () => {
    if (!createForm) return;

    // The web only creates ORG/COMPANY folder TEMPLATES, and only for admins.
    // Device-bound (personal) folders are created from the desktop/CLI client,
    // which sends X-Device-ID; we never create those here.
    const canCreateTemplate =
      (effectiveMode === 'admin' || effectiveMode === 'sysadmin') &&
      !!user && (user.role === 'admin' || user.role === 'sysadmin');
    if (!canCreateTemplate) {
      toast.error('Auto-encrypt folders are created from your desktop/CLI app. The web creates company templates (admins only).');
      return;
    }

    const req: any = {
      name: createForm.name,
      policy_id: createForm.policy_id,
      include_subfolders: createForm.include_subfolders,
      auto_delete_original: createForm.auto_delete_original,
      move_original_file: createForm.move_original_file,
      move_original_to_path: createForm.move_original_file ? (createForm.move_original_to_path || '') : '',
      is_template: true,
      type: 'company',
      auto_provision_to_members: !!createForm.auto_provision_to_members,
    };
    if (user!.role === 'admin' && user!.company_id) {
      req.company_id = user!.company_id;
    } else if (user!.role === 'sysadmin' && createForm.for_companies && createForm.for_companies.length > 0) {
      req.for_companies = createForm.for_companies;
    }

    try {
      setCreating(true);
      await apiService.createAutoEncryptFolderTemplate(req);
      setShowCreateModal(false);
      setCreateForm(null);
      // Refresh list
      queryClient.invalidateQueries({ queryKey: ['auto-encrypt-folders'] });
      toast.success('Folder template created');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'Failed to create folder template');
    } finally {
      setCreating(false);
    }
  };

  // Update folder mutation
  const updateFolderMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAutoEncryptFolderRequest }) =>
      apiService.updateAutoEncryptFolder(id, data),
    onSuccess: (data, variables) => {
      console.log('✅ FOLDERS: Successfully updated folder:', variables.id, data);
      queryClient.invalidateQueries({ queryKey: ['auto-encrypt-folders'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setEditingFolder(null);
      setEditForm({});
      toast.success('Folder updated successfully');
    },
    onError: (error: any, variables) => {
      console.error('❌ FOLDERS: Failed to update folder:', variables.id, error);
      toast.error(error.response?.data?.error || error.message || 'Failed to update folder');
    },
  });

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteAutoEncryptFolder(id),
    onSuccess: (data, id) => {
      console.log('✅ FOLDERS: Successfully deleted folder:', id);
      queryClient.invalidateQueries({ queryKey: ['auto-encrypt-folders'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Folder deleted successfully');
    },
    onError: (error: any, id) => {
      console.error('❌ FOLDERS: Failed to delete folder:', id, error);
      toast.error(error.response?.data?.error || 'Failed to delete folder');
    },
  });

  // Delegate folder mutation
  // For templates: calls delete template endpoint (which delegates all materialized folders)
  // For materialized folders: calls delegate endpoint (which changes type from company to user)
  const delegateFolderMutation = useMutation({
    mutationFn: async ({ id, isTemplate }: { id: string; isTemplate: boolean }) => {
      if (isTemplate) {
        // For templates, use delete template endpoint which delegates all materialized folders
        console.log('🔄 FOLDERS: Delegating template (will delegate all materialized folders):', id);
        await apiService.deleteAutoEncryptFolderTemplate(id);
      } else {
        // For materialized folders, use delegate endpoint
        console.log('🔄 FOLDERS: Delegating materialized folder:', id);
        await apiService.delegateAutoEncryptFolder(id);
      }
    },
    onSuccess: (data, variables) => {
      console.log('✅ FOLDERS: Successfully delegated folder:', variables.id);
      queryClient.invalidateQueries({ queryKey: ['auto-encrypt-folders'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success(variables.isTemplate 
        ? 'Template deleted and all materialized folders delegated to users' 
        : 'Folder delegated to user successfully');
    },
    onError: (error: any, variables) => {
      console.error('❌ FOLDERS: Failed to delegate folder:', variables.id, error);
      toast.error(error.response?.data?.error || 'Failed to delegate folder');
    },
  });

  const handleEdit = (folder: AutoEncryptFolder) => {
    console.log('✏️ FOLDERS: Starting edit for folder:', folder.id, folder.folder_path);
    closeFolderMenu();
    setEditingFolder(folder.id);
    setShowEditMovePathPicker(false);
    setEditMovePathQuery('');
    setEditForm({
      policy_id: folder.policy_id,
      is_active: folder.is_active,
      include_subfolders: folder.include_subfolders,
      auto_delete_original: folder.auto_delete_original,
      move_original_file: !!folder.move_original_file,
      move_original_to_path: folder.move_original_file ? (folder.move_original_to_path || '') : '',
      auto_provision_to_members: folder.auto_provision_to_members,
    } as any);
  };

  const handleSave = () => {
    if (editingFolder) {
      console.log('💾 FOLDERS: Saving changes for folder:', editingFolder, 'with data:', editForm);
      updateFolderMutation.mutate({ id: editingFolder, data: editForm });
    }
  };

  const handleCancel = () => {
    console.log('❌ FOLDERS: Cancelling edit for folder:', editingFolder);
    setEditingFolder(null);
    closeFolderMenu();
    setShowEditMovePathPicker(false);
    setEditMovePathQuery('');
    setEditForm({});
  };

  const closeFolderMenu = () => setOpenFolderMenuId(null);

  const handleDelete = (id: string, path: string) => {
    console.log('🗑️ FOLDERS: Delete requested for folder:', id, path);
    setFolderToDelete({ id, path });
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (folderToDelete) {
      console.log('🗑️ FOLDERS: Confirmed delete for folder:', folderToDelete.id);
      deleteFolderMutation.mutate(folderToDelete.id);
      setDeleteModalOpen(false);
      setFolderToDelete(null);
    }
  };

  const cancelDelete = () => {
    console.log('❌ FOLDERS: Delete cancelled for folder:', folderToDelete?.id);
    setDeleteModalOpen(false);
    setFolderToDelete(null);
  };

  const handleDelegate = (folder: AutoEncryptFolder) => {
    const name = folder.name || folder.folder_path || 'unnamed';
    console.log('🔄 FOLDERS: Delegate requested for folder:', folder.id, name, 'is_template:', folder.is_template);
    setFolderToDelegate({ id: folder.id, name, is_template: folder.is_template || false });
    setDelegateModalOpen(true);
  };

  const confirmDelegate = () => {
    if (folderToDelegate) {
      console.log('🔄 FOLDERS: Confirmed delegate for folder:', folderToDelegate.id, 'is_template:', folderToDelegate.is_template);
      delegateFolderMutation.mutate({ id: folderToDelegate.id, isTemplate: folderToDelegate.is_template });
      setDelegateModalOpen(false);
      setFolderToDelegate(null);
    }
  };

  const cancelDelegate = () => {
    console.log('❌ FOLDERS: Delegate cancelled for folder:', folderToDelegate?.id);
    setDelegateModalOpen(false);
    setFolderToDelegate(null);
  };

  const getPolicyName = (policyId: string) => {
    const policy = policies.find(p => p.id === policyId);
    return policy?.name || 'Unknown Policy';
  };

  // Render folder item (extracted for reuse in accordion)
  // Check if folder is editable by current user
  const isFolderEditable = (folder: AutoEncryptFolder): boolean => {
    // Admins and sysadmins can edit all folders
    if (user && (['admin', 'sysadmin'] as any[]).includes(user.role as any)) {
      return true;
    }
    // Regular users can only edit their own user-type folders
    return folder.type === 'user';
  };

  const renderFolderItem = (folder: AutoEncryptFolder) => {
    const editable = isFolderEditable(folder);
    const menuOpen = openFolderMenuId === folder.id;
    
    // Debug: Log folder details for admins/sysadmins
    if (user && (user.role === 'admin' || user.role === 'sysadmin')) {
      console.log('🔍 FOLDERS: Rendering folder for admin:', {
        id: folder.id,
        name: folder.name,
        type: folder.type,
        is_template: folder.is_template,
        shouldShowDelegate: folder.type === 'company' && !folder.is_template
      });
    }
    
    return (
    <div className="px-4 py-4 sm:px-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 flex items-center space-x-3">
            <FolderIcon className={`h-8 w-8 ${folder.is_active ? 'text-green-500' : 'text-gray-400'}`} />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {folder.name || folder.folder_path || '(unnamed)'}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2">
              {/* Name/path already shown next to icon; keep this empty to avoid duplication */}
            </div>
            <div className="hidden lg:flex mt-1 items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
              <div className="flex items-center">
                <span className={`${getStatusColor(folder.provisioning_state as any)} flex items-center`}>
                  {getStatusIcon(folder.provisioning_state as any)}
                  <span className="ml-1">{formatProvisioning(folder.provisioning_state)}</span>
                </span>
              </div>
              <div className="flex items-center">
                <span className="font-medium mr-1">Policy:</span>{' '}
                <Link
                  to={`/policies/${folder.policy_id}`}
                  className="text-governate-600 hover:underline"
                  title="View policy"
                >
                  {getPolicyName(folder.policy_id)}
                </Link>
              </div>
              <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${folder.include_subfolders ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                Include subfolders
              </div>
              <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${folder.auto_delete_original ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                Auto delete original
              </div>
            </div>
          </div>
        </div>
        {editable && (
          <div className="flex items-center space-x-3">
            {/* Status Toggle - only show when not editing and folder is editable */}
            {editingFolder !== folder.id && (
              <div className="flex items-center space-x-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!folder.is_active}
                    onChange={(e) => {
                      updateFolderMutation.mutate({ 
                        id: folder.id, 
                        data: { is_active: e.target.checked } 
                      });
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600"></div>
                  <span className="hidden lg:inline ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                    {folder.is_active ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>
            )}
            
            {/* Mobile kebab menu (compact UI) */}
            {editingFolder !== folder.id && (
              <div className="relative lg:hidden">
                <button
                  type="button"
                  onClick={() => setOpenFolderMenuId(menuOpen ? null : folder.id)}
                  className="inline-flex items-center p-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50"
                  aria-label="Open folder menu"
                  title="Menu"
                >
                  <EllipsisVerticalIcon className="w-5 h-5" />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-72 rounded-md border border-gray-200 bg-white shadow-lg z-50 overflow-hidden">
                    <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
                      Details
                    </div>
                    <div className="px-3 py-2 text-sm text-gray-700 space-y-1">
                      <div><span className="font-medium">Status:</span> {formatProvisioning(folder.provisioning_state)}</div>
                      <div>
                        <span className="font-medium">Policy:</span>{' '}
                        <Link
                          to={`/policies/${folder.policy_id}`}
                          className="text-governate-600 hover:underline"
                          onClick={closeFolderMenu}
                        >
                          {getPolicyName(folder.policy_id)}
                        </Link>
                      </div>
                      <div><span className="font-medium">Include subfolders:</span> {folder.include_subfolders ? 'Yes' : 'No'}</div>
                      <div><span className="font-medium">Auto delete original:</span> {folder.auto_delete_original ? 'Yes' : 'No'}</div>
                      <div><span className="font-medium">Move original:</span> {(folder as any).move_original_file ? 'Yes' : 'No'}</div>
                      {(folder as any).move_original_file && (
                        <div><span className="font-medium">Move to:</span> {(folder as any).move_original_to_path || ''}</div>
                      )}
                      <div><span className="font-medium">Created:</span> {folder.created_at ? new Date(folder.created_at).toLocaleString() : '-'}</div>
                      <div><span className="font-medium">Updated:</span> {folder.updated_at ? new Date(folder.updated_at).toLocaleString() : '-'}</div>
                    </div>
                    <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
                      Actions
                    </div>
                    <div className="p-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { closeFolderMenu(); handleEdit(folder); }}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      {(() => {
                        const isCompanyFolder = folder.type === 'company';
                        const isAdminOrSysadmin = user && (user.role === 'admin' || user.role === 'sysadmin');
                        if (isCompanyFolder && isAdminOrSysadmin) {
                          return (
                            <button
                              type="button"
                              onClick={() => { closeFolderMenu(); handleDelegate(folder); }}
                              disabled={delegateFolderMutation.isPending}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-blue-600 rounded text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                              Delegate
                            </button>
                          );
                        }
                        return (
                          <button
                            type="button"
                            onClick={() => { closeFolderMenu(); handleDelete(folder.id, folder.folder_path); }}
                            disabled={deleteFolderMutation.isPending}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-600 rounded text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="hidden lg:flex items-center space-x-2">
              {editingFolder === folder.id ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={updateFolderMutation.isPending}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-governate-600 hover:bg-governate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50"
                  >
                    <CheckIcon className="w-4 h-4 mr-1" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500"
                  >
                    <XMarkIcon className="w-4 h-4 mr-1" />
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleEdit(folder)}
                    className="inline-flex items-center p-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  {/* For admins/sysadmins: show delegate button for company-type folders (both templates and materialized) */}
                  {/* For other folder types or non-admin users: show delete button */}
                  {(() => {
                    const isCompanyFolder = folder.type === 'company';
                    const isAdminOrSysadmin = user && (user.role === 'admin' || user.role === 'sysadmin');
                    
                    if (isCompanyFolder && isAdminOrSysadmin) {
                      // Company-type folder (template or materialized): show delegate button
                      return (
                        <button
                          onClick={() => handleDelegate(folder)}
                          disabled={delegateFolderMutation.isPending}
                          className="inline-flex items-center p-2 border border-transparent rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          title={folder.is_template ? "Delegate all materialized folders to users" : "Delegate to User"}
                        >
                          <ArrowsRightLeftIcon className="w-4 h-4" />
                        </button>
                      );
                    } else {
                      // Other folder types or non-admin users: show delete button
                      return (
                        <button
                          onClick={() => handleDelete(folder.id, folder.folder_path)}
                          disabled={deleteFolderMutation.isPending}
                          className="inline-flex items-center p-2 border border-transparent rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      );
                    }
                  })()}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Form - only show if folder is editable */}
      {editingFolder === folder.id && editable && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Policy Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Encryption Policy
              </label>
              <select
                value={editForm.policy_id || ''}
                onChange={(e) => setEditForm({ ...editForm, policy_id: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-governate-500 focus:border-governate-500 sm:text-sm"
              >
                {policies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Include Subfolders - Toggle Switch */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Include Subfolders
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editForm.include_subfolders}
                  onChange={(e) => setEditForm({ ...editForm, include_subfolders: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {editForm.include_subfolders ? 'Yes' : 'No'}
                </span>
              </label>
            </div>

            {/* Auto Delete Original - Toggle Switch */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Auto Delete Original
              </label>
              <div className="relative inline-flex items-center group">
                <label className={`relative inline-flex items-center ${(editForm as any).move_original_file ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={!!editForm.auto_delete_original}
                    onChange={(e) => {
                      if ((editForm as any).move_original_file) return;
                      setEditForm({ ...editForm, auto_delete_original: e.target.checked });
                    }}
                    disabled={!!(editForm as any).move_original_file}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600 ${(editForm as any).move_original_file ? 'opacity-50' : ''}`}></div>
                  <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {editForm.auto_delete_original ? 'Yes' : 'No'}
                  </span>
                </label>
                {(editForm as any).move_original_file && (
                  <div className="absolute left-0 top-8 z-10 hidden group-hover:block">
                    <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                      Disable "Move Original File" first to enable this option
                      <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Move Original File - Toggle Switch + conditional path */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Move Original File
              </label>
              <div className="relative inline-flex items-center group">
                <label className={`relative inline-flex items-center ${editForm.auto_delete_original ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={!!(editForm as any).move_original_file}
                    onChange={(e) => {
                      if (editForm.auto_delete_original) return;
                      const enabled = e.target.checked;
                      setEditForm(prev => ({
                        ...prev,
                        move_original_file: enabled,
                        move_original_to_path: enabled ? ((prev as any).move_original_to_path || '') : '',
                      }) as any);
                    }}
                    disabled={!!editForm.auto_delete_original}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600 ${editForm.auto_delete_original ? 'opacity-50' : ''}`}></div>
                  <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {(editForm as any).move_original_file ? 'Yes' : 'No'}
                  </span>
                </label>
                {editForm.auto_delete_original && (
                  <div className="absolute left-0 top-8 z-10 hidden group-hover:block">
                    <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                      Disable "Auto Delete Original" first to enable this option
                      <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                    </div>
                  </div>
                )}
              </div>

              {!!(editForm as any).move_original_file && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Move Original To (folder name / path)
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={(editForm as any).move_original_to_path || ''}
                        onChange={(e) => setEditForm(prev => ({ ...(prev as any), move_original_to_path: e.target.value }) as any)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-governate-500 focus:border-governate-500 sm:text-sm"
                        placeholder="e.g. Archive"
                      />
                      {moveToPathSuggestions.length > 0 && (
                        <button
                          type="button"
                          onClick={() => { setShowEditMovePathPicker(true); setEditMovePathQuery(''); }}
                          className="shrink-0 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                          title="Pick from existing paths"
                          aria-label="Pick from existing paths"
                        >
                          <MagnifyingGlassIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Auto-create on registration (templates only, admin/sysadmin) - Toggle Switch */}
            {(folder.is_template && user && (['admin','sysadmin'] as any).includes((user.role as any))) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {folder.type === 'company' ? 'Auto-provision to company members' : 'Auto-provision to members'}
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!(editForm as any).auto_provision_to_members}
                    onChange={(e) => setEditForm({ ...editForm, auto_provision_to_members: e.target.checked } as any)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {(editForm as any).auto_provision_to_members ? 'Yes' : 'No'}
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Condensed layout already shows details; optionally show last processed */}
      {editingFolder !== folder.id && folder.last_processed_at && (
        <div className="mt-2 text-sm text-gray-500">
          <span className="font-medium">Last Processed:</span> {new Date(folder.last_processed_at).toLocaleString()}
        </div>
      )}
    </div>
    );
  };

  const renderEditMovePathModal = () => {
    if (!showEditMovePathPicker) return null;
    if (moveToPathSuggestions.length === 0) return null;

    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={() => { setShowEditMovePathPicker(false); setEditMovePathQuery(''); }}>
        <div className="bg-white rounded-lg p-6 w-full max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Select Move-To Path</h3>
            <button
              type="button"
              onClick={() => { setShowEditMovePathPicker(false); setEditMovePathQuery(''); }}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={editMovePathQuery}
              onChange={(e) => setEditMovePathQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
              placeholder="Type to filter…"
              autoFocus
            />
          </div>

          <div className="border border-gray-200 rounded-md max-h-[60vh] overflow-auto">
            {(filteredEditMoveToPathSuggestions.length > 0 ? filteredEditMoveToPathSuggestions : moveToPathSuggestions).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setEditForm(prev => ({ ...(prev as any), move_original_to_path: p }) as any);
                  setShowEditMovePathPicker(false);
                  setEditMovePathQuery('');
                }}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const getStatusColor = (state: string) => {
    switch (state) {
      case 'requested': return 'text-gray-500';
      case 'awaiting_client': return 'text-blue-500';
      case 'ready': return 'text-green-500';
      case 'stale': return 'text-yellow-600';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (state: string) => {
    switch (state) {
      case 'requested': return <ClockIcon className="w-4 h-4" />;
      case 'awaiting_client': return <ClockIcon className="w-4 h-4" />;
      case 'ready': return <CheckIcon className="w-4 h-4" />;
      case 'stale': return <ExclamationTriangleIcon className="w-4 h-4" />;
      default: return <ClockIcon className="w-4 h-4" />;
    }
  };

  const formatProvisioning = (state?: string) => {
    switch (state) {
      case 'requested': return 'Requested';
      case 'awaiting_client': return 'Awaiting client';
      case 'ready': return 'Ready';
      case 'stale': return 'Stale';
      default: return 'Requested';
    }
  };

  // Filter folders based on type filter (only for regular users on device page)
  // Must be called before any early returns to comply with React hooks rules
  const filteredFolders = useMemo(() => {
    if (deviceId && user?.role === 'user') {
      // If both selected or both unselected, show all
      if ((filterUser && filterCompany) || (!filterUser && !filterCompany)) {
        return folders;
      }
      // If only one is selected, filter by that type
      if (filterUser && !filterCompany) {
        return folders.filter(folder => folder.type === 'user');
      }
      if (filterCompany && !filterUser) {
        return folders.filter(folder => folder.type === 'company');
      }
    }
    return folders;
  }, [folders, filterUser, filterCompany, deviceId, user?.role]);

  if (devicesError || foldersError) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading data</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
      </div>
    );
  }
  
  // If no device is selected and user is regular user, show device selection
  if (!deviceId && user?.role === 'user') {
    return (
      <div className="space-y-6">
        {/* Header */}
   

        {/* Info Banner */}
        {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FolderIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Auto-Encrypt Folders
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  These folders are monitored by your desktop client and automatically encrypt files when they are added.
                  Select a device below to view and manage its folders.
                </p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Device Cards */}
        {desktopDevices.length === 0 ? (
          <div className="text-center py-12">
            <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No desktop devices found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Desktop devices will appear here once you log in from the desktop client.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {desktopDevices.map((device) => {
              const deviceFolders = allFolders.filter(folder => folder.device_id === device.id);
              return (
                <div
                  key={device.id}
                  onClick={() => handleDeviceSelect(device.id)}
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:border-governate-300 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <ComputerDesktopIcon className="h-8 w-8 text-governate-500" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {device.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {device.type || 'Desktop'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      {deviceFolders.length} folder{deviceFolders.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-xs text-gray-400">
                      Click to manage
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Create Folder Modal on root folders page */}
        {showCreateModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={() => setShowCreateModal(false)}>
            <div className="bg-white rounded-lg p-6 w-full max-w-md md:max-w-2xl relative" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Auto-Encrypt Folder</h3>

              <div className="space-y-4">
                {/* Device selection for root page (regular users) */}
                {user?.role === 'user' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Device</label>
                    <select
                      value={createForm?.device_id || ''}
                      onChange={(e) => setCreateForm(prev => prev ? { ...prev, device_id: e.target.value } : prev)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                    >
                      <option value="">Select device...</option>
                      {desktopDevices.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Admin notice - only show in administration view */}
                {(() => {
                  if ((effectiveMode !== 'admin' && effectiveMode !== 'sysadmin') || !user) return null;
                  const userRole = user.role as 'admin' | 'sysadmin' | 'user';
                  if (!['admin', 'sysadmin'].includes(userRole)) return null;
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        This folder will be created as a template for company usage.
                      </p>
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={createForm?.name || ''}
                    onChange={(e) => setCreateForm(prev => prev ? { ...prev, name: e.target.value } : prev)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                    placeholder="Folder name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy</label>
                  <select
                    value={createForm?.policy_id || ''}
                    onChange={(e) => setCreateForm(prev => prev ? { ...prev, policy_id: e.target.value } : prev)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  >
                    <option value="">Select policy...</option>
                    {policies.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Include Subfolders - Toggle Switch */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Include Subfolders
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!createForm?.include_subfolders}
                      onChange={(e) => setCreateForm(prev => prev ? { ...prev, include_subfolders: e.target.checked } : prev)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {createForm?.include_subfolders ? 'Yes' : 'No'}
                    </span>
                  </label>
                </div>

                {/* Auto Delete Original - Toggle Switch */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Auto Delete Original
                  </label>
                  <div className="relative inline-flex items-center group">
                    <label className={`relative inline-flex items-center ${createForm?.move_original_file ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={!!createForm?.auto_delete_original}
                        onChange={(e) => {
                          if (createForm?.move_original_file) return;
                          setCreateForm(prev => prev ? { ...prev, auto_delete_original: e.target.checked } : prev);
                        }}
                        disabled={!!createForm?.move_original_file}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600 ${createForm?.move_original_file ? 'opacity-50' : ''}`}></div>
                      <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {createForm?.auto_delete_original ? 'Yes' : 'No'}
                      </span>
                    </label>
                    {createForm?.move_original_file && (
                      <div className="absolute left-0 top-8 z-10 hidden group-hover:block">
                        <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                          Disable "Move Original File" first to enable this option
                          <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Move Original File - Toggle + conditional path */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Move Original File
                  </label>
                  <div className="relative inline-flex items-center group">
                    <label className={`relative inline-flex items-center ${createForm?.auto_delete_original ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={!!createForm?.move_original_file}
                        onChange={(e) => {
                          if (createForm?.auto_delete_original) return;
                          if (!createForm) return;
                          const enabled = e.target.checked;
                          setCreateForm({
                            ...createForm,
                            move_original_file: enabled,
                            move_original_to_path: enabled ? (createForm.move_original_to_path || '') : '',
                          });
                        }}
                        disabled={!!createForm?.auto_delete_original}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600 ${createForm?.auto_delete_original ? 'opacity-50' : ''}`}></div>
                      <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {createForm?.move_original_file ? 'Yes' : 'No'}
                      </span>
                    </label>
                    {createForm?.auto_delete_original && (
                      <div className="absolute left-0 top-8 z-10 hidden group-hover:block">
                        <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                          Disable "Auto Delete Original" first to enable this option
                          <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {createForm?.move_original_file && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Move Original To (folder name / path)
                      </label>
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={createForm?.move_original_to_path || ''}
                            onChange={(e) => setCreateForm(prev => prev ? { ...prev, move_original_to_path: e.target.value } : prev)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                            placeholder="e.g. Archive"
                          />
                          {moveToPathSuggestions.length > 0 && (
                            <button
                              type="button"
                              onClick={() => { setShowCreateMovePathPicker(true); setCreateMovePathQuery(''); }}
                              className="shrink-0 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                              title="Pick from existing paths"
                              aria-label="Pick from existing paths"
                            >
                              <MagnifyingGlassIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Auto-provision to members (admin/sysadmin only) - Only show in admin/sysadmin mode */}
                {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (user && (['admin','sysadmin'] as any).includes((user.role as any))) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {(user.role as any) === 'admin' ? 'Auto-provision to company members' : 'Auto-provision to members'}
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!createForm?.auto_provision_to_members}
                        onChange={(e) => setCreateForm(prev => prev ? { ...prev, auto_provision_to_members: e.target.checked } : prev)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {createForm?.auto_provision_to_members ? 'Yes' : 'No'}
                      </span>
                    </label>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={() => { setShowCreateModal(false); setShowCreateMovePathPicker(false); setCreateMovePathQuery(''); }} className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-md">
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={
                    creating ||
                    !createForm?.name ||
                    !createForm?.policy_id ||
                    (user?.role === 'user' && !createForm?.device_id) ||
                    (!!createForm?.move_original_file && !(createForm?.move_original_to_path || '').trim())
                  }
                  className="px-4 py-2 bg-governate-600 text-white rounded-md disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>

              {/* Move-to-path picker (replaces modal content area; scrolls internally) */}
              {showCreateMovePathPicker && moveToPathSuggestions.length > 0 && (
                <div className="absolute inset-0 bg-white rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Select Move-To Path</h3>
                    <button
                      type="button"
                      onClick={() => { setShowCreateMovePathPicker(false); setCreateMovePathQuery(''); }}
                      className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <input
                      type="text"
                      value={createMovePathQuery}
                      onChange={(e) => setCreateMovePathQuery(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder="Type to filter…"
                      autoFocus
                    />
                  </div>

                  <div className="border border-gray-200 rounded-md max-h-[60vh] overflow-auto">
                    {(filteredMoveToPathSuggestions.length > 0 ? filteredMoveToPathSuggestions : moveToPathSuggestions).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => {
                          setCreateForm(prev => prev ? { ...prev, move_original_to_path: p } : prev);
                          setShowCreateMovePathPicker(false);
                          setCreateMovePathQuery('');
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {renderEditMovePathModal()}
      {/* Header summary with count on left and filter buttons on right (for regular users on device page) */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {filteredFolders.length} folder{filteredFolders.length !== 1 ? 's' : ''}
        </div>
        <div className="flex items-center space-x-3">
          {/* Filter buttons - only show for regular users on device page */}
          {deviceId && user?.role === 'user' && (
            <div className="flex items-center space-x-0 border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setFilterUser(prev => !prev)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  filterUser
                    ? 'bg-governate-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                User
              </button>
              <button
                onClick={() => setFilterCompany(prev => !prev)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                  filterCompany
                    ? 'bg-governate-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Company
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Folders List */}
      {filteredFolders.length === 0 ? (
    <div className="text-center py-12">
          <FolderOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No auto-encrypt folders</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create your first auto-encrypt folder from the desktop client.
          </p>
        </div>
      ) : user?.role === 'sysadmin' && foldersByCompany ? (
        // Sysadmin: Group by company with accordion
        <div className="space-y-4">
          {Object.entries(foldersByCompany.grouped).map(([companyId, companyFolders]) => {
            const company = companies.find(c => c.id === companyId);
            const isExpanded = expandedCompanies[companyId] ?? false;
            return (
              <div key={companyId} className="bg-white shadow overflow-visible lg:overflow-hidden lg:rounded-lg border border-gray-200">
                <button
                  onClick={() => setExpandedCompanies(prev => ({ ...prev, [companyId]: !isExpanded }))}
                  className="w-full px-4 py-5 sm:px-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 mr-2 text-gray-600" />
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {company?.name || 'Unknown Company'}
                    </h3>
                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {companyFolders.length}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {isExpanded && (
                  <div className="border-t border-gray-200">
                    <ul className="divide-y divide-gray-200">
                      {companyFolders.map((folder) => (
                        <li key={folder.id}>
                          {renderFolderItem(folder)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
          {foldersByCompany.noCompany.length > 0 && (
            <div className="bg-white shadow overflow-visible lg:overflow-hidden lg:rounded-lg border border-gray-200">
              <button
                onClick={() => setExpandedCompanies(prev => ({ ...prev, 'no-company': !(prev['no-company'] ?? false) }))}
                className="w-full px-4 py-5 sm:px-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <FolderIcon className="h-5 w-5 mr-2 text-gray-600" />
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    No Company
                  </h3>
                  <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {foldersByCompany.noCompany.length}
                  </span>
                </div>
                {expandedCompanies['no-company'] === true ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {expandedCompanies['no-company'] === true && (
                <div className="border-t border-gray-200">
                  <ul className="divide-y divide-gray-200">
                    {foldersByCompany.noCompany.map((folder) => (
                      <li key={folder.id}>
                        {renderFolderItem(folder)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white shadow overflow-visible lg:overflow-hidden lg:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredFolders.map((folder) => (
              <li key={folder.id}>
                {renderFolderItem(folder)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Auto-Encrypt Folder</h3>

            <div className="space-y-4">
              {/* Device selection when not on device page and not creating template */}
              {!deviceId && user?.role === 'user' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Device</label>
                  <select
                    value={createForm?.device_id || ''}
                    onChange={(e) => setCreateForm(prev => prev ? { ...prev, device_id: e.target.value } : prev)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  >
                    <option value="">Select device...</option>
                    {desktopDevices.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Company autocomplete for sysadmin */}
              {user?.role === 'sysadmin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Companies</label>
                  
                  {/* Selected Companies as Chips */}
                  {selectedCompanies.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {selectedCompanies.map((companyId) => {
                        const company = companies.find(c => c.id === companyId);
                        return (
                          <div
                            key={companyId}
                            className="inline-flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5"
                          >
                            <BuildingOfficeIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm text-blue-700 dark:text-blue-300">{company?.name || 'Unknown'}</span>
                            <button
                              onClick={() => {
                                const newSelected = selectedCompanies.filter(id => id !== companyId);
                                setSelectedCompanies(newSelected);
                                setCreateForm(prev => prev ? { ...prev, for_companies: newSelected } : prev);
                              }}
                              className="text-blue-400 dark:text-blue-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              title="Remove company"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Autocomplete Input */}
                  <div className="relative">
                    <input
                      type="text"
                      value={companySearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCompanySearchTerm(value);
                        
                        // Filter companies based on search term
                        if (value.trim()) {
                          const filtered = companies.filter(company => 
                            company.name.toLowerCase().includes(value.toLowerCase()) &&
                            !selectedCompanies.includes(company.id)
                          );
                          setCompanySuggestions(filtered);
                          setShowCompanySuggestions(filtered.length > 0);
                          setSelectedCompanyIndex(-1);
                        } else {
                          setCompanySuggestions([]);
                          setShowCompanySuggestions(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setSelectedCompanyIndex(prev => 
                            prev < companySuggestions.length - 1 ? prev + 1 : prev
                          );
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setSelectedCompanyIndex(prev => prev > 0 ? prev - 1 : -1);
                        } else if (e.key === 'Enter' && selectedCompanyIndex >= 0 && companySuggestions[selectedCompanyIndex]) {
                          e.preventDefault();
                          const company = companySuggestions[selectedCompanyIndex];
                          if (!selectedCompanies.includes(company.id)) {
                            const newSelected = [...selectedCompanies, company.id];
                            setSelectedCompanies(newSelected);
                            setCreateForm(prev => prev ? { ...prev, for_companies: newSelected } : prev);
                            setCompanySearchTerm('');
                            setCompanySuggestions([]);
                            setShowCompanySuggestions(false);
                            setSelectedCompanyIndex(-1);
                          }
                        } else if (e.key === 'Escape') {
                          setShowCompanySuggestions(false);
                          setSelectedCompanyIndex(-1);
                        }
                      }}
                      onFocus={() => {
                        if (companySearchTerm.trim() && companySuggestions.length > 0) {
                          setShowCompanySuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow click events on suggestions
                        setTimeout(() => {
                          setShowCompanySuggestions(false);
                          setSelectedCompanyIndex(-1);
                        }, 200);
                      }}
                      placeholder="Type to search companies..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                    />
                    
                    {/* Autocomplete Suggestions */}
                    {showCompanySuggestions && companySuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {companySuggestions.map((company, index) => (
                          <button
                            key={company.id}
                            onClick={() => {
                              if (!selectedCompanies.includes(company.id)) {
                                const newSelected = [...selectedCompanies, company.id];
                                setSelectedCompanies(newSelected);
                                setCreateForm(prev => prev ? { ...prev, for_companies: newSelected } : prev);
                                setCompanySearchTerm('');
                                setCompanySuggestions([]);
                                setShowCompanySuggestions(false);
                                setSelectedCompanyIndex(-1);
                              }
                            }}
                            className={`w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${
                              index === selectedCompanyIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
                            }`}
                          >
                            <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                            <span>{company.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <p className="mt-1 text-xs text-gray-500">
                    Type to search and select companies. Use arrow keys and Enter to select. If none selected, template will be created without company association.
                  </p>
                </div>
              )}

              {/* For admin, indicate template creation - only in admin/sysadmin mode */}
              {!deviceId && (effectiveMode === 'admin' || effectiveMode === 'sysadmin') && user && user.role === 'admin' && (
                <div className="text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded p-2">
                  This folder will be created as a template for company usage.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={createForm?.name || ''}
                  onChange={(e) => setCreateForm(prev => prev ? { ...prev, name: e.target.value } : prev)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  placeholder="Folder name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Policy</label>
                <select
                  value={createForm?.policy_id || ''}
                  onChange={(e) => setCreateForm(prev => prev ? { ...prev, policy_id: e.target.value } : prev)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                >
                  <option value="">Select policy...</option>
                  {policies.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Include Subfolders - Toggle Switch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Include Subfolders
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!createForm?.include_subfolders}
                    onChange={(e) => setCreateForm(prev => prev ? { ...prev, include_subfolders: e.target.checked } : prev)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {createForm?.include_subfolders ? 'Yes' : 'No'}
                  </span>
                </label>
              </div>

              {/* Auto Delete Original - Toggle Switch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Auto Delete Original
                </label>
                <div className="relative inline-flex items-center group">
                  <label className={`relative inline-flex items-center ${createForm?.move_original_file ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={!!createForm?.auto_delete_original}
                      onChange={(e) => {
                        if (createForm?.move_original_file) return;
                        setCreateForm(prev => prev ? { ...prev, auto_delete_original: e.target.checked } : prev);
                      }}
                      disabled={!!createForm?.move_original_file}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600 ${createForm?.move_original_file ? 'opacity-50' : ''}`}></div>
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {createForm?.auto_delete_original ? 'Yes' : 'No'}
                    </span>
                  </label>
                  {createForm?.move_original_file && (
                    <div className="absolute left-0 top-8 z-10 hidden group-hover:block">
                      <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                        Disable "Move Original File" first to enable this option
                        <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Move Original File - Toggle + conditional path */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Move Original File
                </label>
                <div className="relative inline-flex items-center group">
                  <label className={`relative inline-flex items-center ${createForm?.auto_delete_original ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={!!createForm?.move_original_file}
                      onChange={(e) => {
                        if (createForm?.auto_delete_original) return;
                        if (!createForm) return;
                        const enabled = e.target.checked;
                        setCreateForm({
                          ...createForm,
                          move_original_file: enabled,
                          move_original_to_path: enabled ? (createForm.move_original_to_path || '') : '',
                        });
                      }}
                      disabled={!!createForm?.auto_delete_original}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600 ${createForm?.auto_delete_original ? 'opacity-50' : ''}`}></div>
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {createForm?.move_original_file ? 'Yes' : 'No'}
                    </span>
                  </label>
                  {createForm?.auto_delete_original && (
                    <div className="absolute left-0 top-8 z-10 hidden group-hover:block">
                      <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                        Disable "Auto Delete Original" first to enable this option
                        <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      </div>
                    </div>
                  )}
                </div>

                {createForm?.move_original_file && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Move Original To (folder name / path)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={createForm?.move_original_to_path || ''}
                        onChange={(e) => setCreateForm(prev => prev ? { ...prev, move_original_to_path: e.target.value } : prev)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        placeholder="e.g. Archive"
                      />
                      {moveToPathSuggestions.length > 0 && (
                        <button
                          type="button"
                          onClick={() => { setShowCreateMovePathPicker(true); setCreateMovePathQuery(''); }}
                          className="shrink-0 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                          title="Pick from existing paths"
                          aria-label="Pick from existing paths"
                        >
                          <MagnifyingGlassIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-provision to members (admin/sysadmin only) - Only show in admin/sysadmin mode */}
              {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (user && (['admin','sysadmin'] as any).includes((user.role as any))) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {(user.role as any) === 'admin' ? 'Auto-provision to company members' : 'Auto-provision to members'}
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!createForm?.auto_provision_to_members}
                      onChange={(e) => setCreateForm(prev => prev ? { ...prev, auto_provision_to_members: e.target.checked } : prev)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-governate-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {createForm?.auto_provision_to_members ? 'Yes' : 'No'}
                    </span>
                  </label>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => {
                setShowCreateModal(false);
                setShowCreateMovePathPicker(false);
                setCreateMovePathQuery('');
                setSelectedCompanies([]);
                setCompanySearchTerm('');
                setCompanySuggestions([]);
                setShowCompanySuggestions(false);
              }} className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-md">
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={
                  creating ||
                  !createForm?.name ||
                  !createForm?.policy_id ||
                  (!deviceId && user?.role === 'user' && !createForm?.device_id) ||
                  (!!createForm?.move_original_file && !(createForm?.move_original_to_path || '').trim())
                }
                className="px-4 py-2 bg-governate-600 text-white rounded-md disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>

            {/* Move-to-path picker (replaces modal content area; scrolls internally) */}
            {showCreateMovePathPicker && moveToPathSuggestions.length > 0 && (
              <div className="absolute inset-0 bg-white rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Select Move-To Path</h3>
                  <button
                    type="button"
                    onClick={() => { setShowCreateMovePathPicker(false); setCreateMovePathQuery(''); }}
                    className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                  <input
                    type="text"
                    value={createMovePathQuery}
                    onChange={(e) => setCreateMovePathQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                    placeholder="Type to filter…"
                    autoFocus
                  />
                </div>

                <div className="border border-gray-200 rounded-md max-h-[60vh] overflow-auto">
                  {(filteredMoveToPathSuggestions.length > 0 ? filteredMoveToPathSuggestions : moveToPathSuggestions).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        setCreateForm(prev => prev ? { ...prev, move_original_to_path: p } : prev);
                        setShowCreateMovePathPicker(false);
                        setCreateMovePathQuery('');
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full border-2 border-red-600">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure to remove auto-encryption feature from the folder?
                </p>

                
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteFolderMutation.isPending}
                  className="px-4 py-2 bg-white text-red-600 border-2 border-red-600 text-base font-medium rounded-md w-24 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleteFolderMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delegate Folder Confirmation Modal */}
      {delegateModalOpen && folderToDelegate && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full border-2 border-blue-600">
                <ArrowsRightLeftIcon className="h-6 w-6 text-blue-600" />
              </div>
              
              <div className="mt-2 px-7 py-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {folderToDelegate.is_template ? 'Delegate Template to Users' : 'Delegate Folder to User'}
                </h3>
                <p className="text-sm text-gray-500 mb-2">
                  {folderToDelegate.is_template ? (
                    <>
                      Are you sure you want to delegate the template <strong>"{folderToDelegate.name}"</strong>?
                      <br />
                      This will delegate <strong>all materialized folders</strong> created from this template to users and remove the template.
                    </>
                  ) : (
                    <>
                      Are you sure you want to delegate the folder <strong>"{folderToDelegate.name}"</strong> to user control?
                    </>
                  )}
                </p>
                {!folderToDelegate.is_template && (
                  <p className="text-sm text-gray-500">
                    This will change the folder type from <strong>company</strong> to <strong>user</strong>, allowing the user to manage it themselves.
                  </p>
                )}
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={cancelDelegate}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelegate}
                  disabled={delegateFolderMutation.isPending}
                  className="px-4 py-2 bg-white text-blue-600 border-2 border-blue-600 text-base font-medium rounded-md w-24 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {delegateFolderMutation.isPending ? 'Delegating...' : 'Delegate'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Folders;
